import React from 'react';
import { DateInput, FormCell, NumberInput, Select, TextInput } from 'ui-toolkit';
import {
  hintTextAmount,
  hintTextDays,
  hintTextPeriod,
  updateBuyerReimbursementComputedFields,
} from '../utilities/commonComputations';
import generateLabelFromDateToDateText from '../utilities/generateLabelFromDateToDateText';
import generatePaymentPeriodOptions from '../utilities/generatePaymentPeriodOptions';
import partsFromDate from '../utilities/partsFromDate';
import { createOnChangeHandlerGenerator } from '../utilities/createOnChangeHandlerGenerator';
import { transformDateInputToValue } from '../utilities/transformDateInputToValue';
import { transformLabelValueToValue } from '../utilities/transformLabelValueToValue';
import IHasItemDataProps from './IHasItemDataProps';
import IHasItemEventProps from './IHasItemEventProps';
import IHasMetaDataModalProps from './IHasMetaDataModalProps';
import IHasMetaDataModalState from './IHasMetaDataModalState';
import { IItemWithoutShowSellerProps } from './IItemProps';
import PurchaseItemMetaDataModal, { IPurchaseItemMetaDataModalRef } from './PurchaseItemMetaDataModal';
import SharedPurchaseItemCells from './SharedPurchaseItemCells';
import './_ModalPurchasePresentationItem.scss';
import { IBuyerReimbursementOtherFromSellerViewItem } from '../../../ui-toolkit/cdItems/interfaces/ICDViewItem';

export interface IBuyerReimbursementOtherFromSellerProps
  extends IItemWithoutShowSellerProps,
    IHasItemDataProps<IBuyerReimbursementOtherFromSellerViewItem>,
    IHasItemEventProps<IBuyerReimbursementOtherFromSellerViewItem>,
    IHasMetaDataModalProps {
  onFocus?: () => void;
  onBlur?: (event: React.FocusEvent) => void;
}

const paymentPeriodAsOptions = generatePaymentPeriodOptions();

type IBuyerReimbursementOtherFromSellerState = IHasMetaDataModalState;

export default class BuyerReimbursementOtherFromSeller extends React.Component<
  IBuyerReimbursementOtherFromSellerProps,
  IBuyerReimbursementOtherFromSellerState
> {
  public state = { expanded: !this.props.disableAutoExpand && this.props.data.new };
  private modal = React.createRef<IPurchaseItemMetaDataModalRef>();
  private firstInput: HTMLInputElement | null = null;

  public render() {
    const { data, onChange, hasEditPermissions } = this.props;
    const fromDate = partsFromDate(data.date_from);
    const toDate = partsFromDate(data.date_to);

    const period = data.billing_period;
    const selectedPaymentPeriodOption = paymentPeriodAsOptions.find((option) => option.value === period);

    const generateDefaultOnChangeEvent = createOnChangeHandlerGenerator(data, onChange);
    const generateComputedOnChangeEvent = createOnChangeHandlerGenerator(
      data,
      (x: IBuyerReimbursementOtherFromSellerViewItem) =>
        onChange(updateBuyerReimbursementComputedFields(x, 'seller_at_closing'))
    );

    const sellerAtClosingOnChangeEvent = generateDefaultOnChangeEvent('seller_at_closing');
    const periodAmountOnChangeEvent = generateComputedOnChangeEvent('period_amount');
    const labelOnChangeEvent = generateComputedOnChangeEvent('label');
    const fromOnChangeEvent = generateComputedOnChangeEvent('date_from', transformDateInputToValue);
    const toOnChangeEvent = generateComputedOnChangeEvent('date_to', transformDateInputToValue);
    const paymentPeriodOnChangeEvent = generateComputedOnChangeEvent('billing_period', transformLabelValueToValue);

    return (
      <SharedPurchaseItemCells
        {...this.props}
        sellerAtClosingDisplay="text"
        className="modal-purchase-presentation-item buyer-reimbursement"
        onBlur={this.onBlur}
        onFocus={this.onFocus}
        hasEditPermissions={hasEditPermissions}
      >
        <PurchaseItemMetaDataModal
          label={generateLabelFromDateToDateText(data.label, fromDate, toDate)}
          onToggle={this.onModalToggleEvent}
          expanded={this.state.expanded}
          ref={this.modal}
        >
          <div className="buyer-reimbursement__fields">
            <TextInput
              className="buyer-reimbursement__fields__full-line"
              label={'Name'}
              onChange={labelOnChangeEvent}
              placeholder="Name"
              getInputRef={this.setFirstInput}
              value={data.label}
              readOnly={!hasEditPermissions}
              debounceOnValueChange
            />

            <FormCell label={'Billing Period'} hintText={hintTextPeriod(data)}>
              <Select
                value={selectedPaymentPeriodOption}
                onChange={paymentPeriodOnChangeEvent}
                options={paymentPeriodAsOptions}
                isClearable={true}
                isDisabled={!hasEditPermissions}
              />
            </FormCell>

            <div>
              <NumberInput
                mode="currency"
                label="Billing Amount"
                onBlur={this.onBlur}
                onFocus={this.onFocus}
                onChange={periodAmountOnChangeEvent}
                value={data.period_amount}
                readOnly={!hasEditPermissions}
                debounceOnValueChange
              />
            </div>

            <DateInput
              label="From Date"
              value={fromDate ? `${fromDate.year}-${fromDate.month}-${fromDate.date}` : null}
              name="date_from"
              onBlur={this.onBlur}
              onChange={fromOnChangeEvent}
              hintText={hintTextDays(data)}
              readOnly={!hasEditPermissions}
            />
            <DateInput
              label="To Date"
              value={toDate ? `${toDate.year}-${toDate.month}-${toDate.date}` : null}
              name="date_to"
              onBlur={this.onBlur}
              onChange={toOnChangeEvent}
              readOnly={!hasEditPermissions}
            />

            <FormCell label="Amount" className="buyer-reimbursement__fields__full-line" hintText={hintTextAmount(data)}>
              <NumberInput
                disabled={!!selectedPaymentPeriodOption}
                mode={'currency'}
                onChange={sellerAtClosingOnChangeEvent}
                onBlur={this.onBlur}
                onFocus={this.onFocus}
                readOnly={!!selectedPaymentPeriodOption || !hasEditPermissions}
                value={data.seller_at_closing}
                debounceOnValueChange
              />
            </FormCell>
          </div>
        </PurchaseItemMetaDataModal>
      </SharedPurchaseItemCells>
    );
  }

  private setFirstInput = (element: HTMLInputElement) => {
    this.firstInput = element;
  };

  private onBlur = (event: React.FocusEvent) => {
    const { onBlur } = this.props;
    const node = this.modal.current;
    if (node && event.relatedTarget && !node.modalContains(event.relatedTarget as HTMLElement)) {
      this.setState({ expanded: false });
    }
    if (onBlur) {
      onBlur(event);
    }
  };

  private onFocus = () => {
    const { onFocus } = this.props;
    if (onFocus) {
      onFocus();
    }
  };

  private onModalToggleEvent = (expanded: boolean) => {
    this.setState({ expanded });
    if (this.firstInput) {
      this.firstInput.focus();
    }
  };
}
